import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * CollapsibleText Component
 * 
 * Renders text with collapse/expand functionality for long content.
 * Shows preview of 3-4 lines (~180 chars) with ellipsis and toggle button.
 * 
 * Global component used across:
 * - Vehicle description on VehicleDetails page
 * - Vehicle rules card
 * - Any other long text content
 */
const CollapsibleText = ({ 
  text, 
  previewLength = 180,
  previewLines = 4, // Number of lines to show in collapsed state
  showMoreLabel = "Show more",
  showLessLabel = "Show less",
  className = "",
  textClassName = "text-slate-600 leading-relaxed"
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [shouldCollapse, setShouldCollapse] = useState(false);
  const contentRef = useRef(null);
  const measureRef = useRef(null);
  
  // Check if text needs collapsing based on both character count and actual rendered height
  useEffect(() => {
    // Character-based check
    const charBasedCollapse = text && text.length > previewLength;
    
    // Also check actual rendered height (for multi-line content)
    if (measureRef.current) {
      const lineHeight = parseInt(window.getComputedStyle(measureRef.current).lineHeight) || 24;
      const maxHeight = lineHeight * previewLines;
      const actualHeight = measureRef.current.scrollHeight;
      const heightBasedCollapse = actualHeight > maxHeight;
      
      setShouldCollapse(charBasedCollapse || heightBasedCollapse);
    } else {
      setShouldCollapse(charBasedCollapse);
    }
  }, [text, previewLength, previewLines]);
  
  if (!text) return null;
  
  // Get preview text - try to break at word boundary
  const getPreviewText = () => {
    if (text.length <= previewLength) return text;
    
    // Find the last space before previewLength to avoid cutting words
    const truncated = text.substring(0, previewLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > previewLength * 0.7) {
      return truncated.substring(0, lastSpaceIndex);
    }
    
    return truncated;
  };
  
  const previewText = getPreviewText();
  
  return (
    <div className={className}>
      {/* Hidden element to measure full text height */}
      <div 
        ref={measureRef}
        className="sr-only absolute opacity-0 pointer-events-none"
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {text}
      </div>
      
      <div 
        ref={contentRef}
        className="overflow-hidden transition-all duration-300 ease-in-out"
      >
        {isExpanded ? (
          // Expanded: show full text
          <p className={`${textClassName} whitespace-pre-wrap`}>
            {text}
          </p>
        ) : (
          // Collapsed: use line-clamp for consistent 3-4 line preview
          <p 
            className={`${textClassName} whitespace-pre-wrap`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: previewLines,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            {text}
          </p>
        )}
      </div>
      
      {shouldCollapse && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-[#004B8D] hover:text-[#003a6d] transition-colors focus:outline-none focus:ring-2 focus:ring-[#004B8D]/20 focus:ring-offset-2 rounded"
          data-testid={isExpanded ? "collapse-text-btn" : "expand-text-btn"}
        >
          {isExpanded ? (
            <>
              {showLessLabel}
              <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              {showMoreLabel}
              <ChevronDown className="w-4 h-4" />
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default CollapsibleText;
